import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus, Trash2, FileText, Send, Printer, X, Calendar, Search, ArrowUpRight, Clock, XCircle, CalendarCheck } from "@/components/icons";
import { useOfficeFilter } from "../../lib/officeFilter";
import { useSettings } from "../../lib/settings";
import { useAuth } from "../../lib/auth";
import { useRealtimeEvent } from "../../lib/realtime";
import Portal from "../../components/Portal";
import AppleModal from "../../components/AppleModal";
import { TableSkeleton } from "../../components/Skeletons";
import AppleKpi from "../../components/AppleKpi";

const emptyItem = {
  medication: "",
  dosage: "",
  frequency: "",
  duration: "",
  quantity: "",
  instructions: "",
};

export default function Prescriptions() {
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [appts, setAppts] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [open, setOpen] = useState(false);
  const { officeId } = useOfficeFilter();
  const { pharmacyEnabled } = useSettings();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Date filter — default = today
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const [dateFilter, setDateFilter] = useState(todayStr);
  // Rango predefinido ("week" / "month"). Null = usa dateFilter o todas.
  const [rangeFilter, setRangeFilter] = useState(null);
  const [form, setForm] = useState({
    patient_id: "",
    diagnosis: "",
    notes: "",
    pharmacy_ids: [],
    items: [],
  });
  const [currentItem, setCurrentItem] = useState({ ...emptyItem });

  const load = async () => {
    const [r, p, ph, a] = await Promise.all([
      api.get("/prescriptions"),
      api.get("/patients"),
      api.get("/users/pharmacies"),
      api.get("/appointments").catch(() => ({ data: [] })),
    ]);
    setList(r.data);
    setPatients(p.data);
    setPharmacies(ph.data);
    setAppts(a.data);
    setFirstLoad(false);
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  // Recarga en tiempo real cuando:
  //  • Se emite una nueva receta (post-consulta, modal, etc.) — desde otra
  //    pestaña o desde el mismo tab.
  //  • La farmacia cambia el status (aceptada / lista / despachada / rechazada).
  useRealtimeEvent("prescription:created", () => load());
  useRealtimeEvent("prescription:status_changed", () => load());

  // Auto-open new prescription modal when ?patient=<id> is in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("patient");
    if (!pid || patients.length === 0) return;
    const match = patients.find((p) => p.id === pid);
    if (match) {
      setForm((f) => ({ ...f, patient_id: pid }));
      setOpen(true);
      // Clean the query string so it doesn't re-trigger
      navigate("/doctor/recetas", { replace: true });
    }
  }, [patients, location.search, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) {
      toast.error("Agrega al menos un medicamento a la receta");
      return;
    }
    try {
      await api.post("/prescriptions", form);
      toast.success("Receta creada y enviada");
      setOpen(false);
      setForm({
        patient_id: "",
        diagnosis: "",
        notes: "",
        pharmacy_ids: [],
        items: [],
      });
      setCurrentItem({ ...emptyItem });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const addCurrentItem = () => {
    if (!currentItem.medication.trim()) {
      toast.error("Ingresa el nombre del medicamento");
      return;
    }
    setForm((f) => ({ ...f, items: [...f.items, currentItem] }));
    setCurrentItem({ ...emptyItem });
  };

  const removeItem = (i) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, k) => k !== i),
    }));
  };

  const togglePharm = (id) => {
    setForm((f) => ({
      ...f,
      pharmacy_ids: f.pharmacy_ids.includes(id)
        ? f.pharmacy_ids.filter((x) => x !== id)
        : [...f.pharmacy_ids, id],
    }));
  };

  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));

  // Map appointment_id -> office_id
  const apptOfficeById = Object.fromEntries(
    appts.map((a) => [a.id, a.office_id || ""])
  );

  // Filter prescriptions by office: include rx where either
  //  (a) the linked appointment belongs to the office, OR
  //  (b) the patient itself belongs to the office (rx without appointment),
  //  (c) if no office info at all, include everything (no filter dropping freeform rx).
  const officeFiltered = officeId
    ? list.filter((rx) => {
        const apptOff = rx.appointment_id ? apptOfficeById[rx.appointment_id] : null;
        const patOff = patientById[rx.patient_id]?.office_id || null;
        if (apptOff) return apptOff === officeId;
        if (patOff) return patOff === officeId;
        return true; // freeform rx with no office info → always visible
      })
    : list;

  // Filter by date range / specific date and sort newest first
  const visibleList = officeFiltered
    .filter((rx) => {
      const d = (rx.created_at || "").slice(0, 10);
      if (rangeFilter === "week") {
        const start = new Date();
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        return new Date(rx.created_at || 0) >= start;
      }
      if (rangeFilter === "month") {
        const start = new Date();
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        return new Date(rx.created_at || 0) >= start;
      }
      if (!dateFilter) return true;
      return d === dateFilter;
    })
    .slice()
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const isToday = !rangeFilter && dateFilter === todayStr;
  const isWeek = rangeFilter === "week";
  const isMonth = rangeFilter === "month";
  const isAll = !rangeFilter && !dateFilter;

  // ── KPI stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = officeFiltered.length;
    const emitidas = officeFiltered.filter((r) => r.status === "issued").length;
    const aceptadas = officeFiltered.filter((r) => ["accepted", "ready", "fulfilled"].includes(r.status)).length;
    const rechazadas = officeFiltered.filter((r) => r.status === "rejected").length;
    // últimos 7 días
    const now = new Date();
    const bucketsWeek = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const count = officeFiltered.filter((r) => (r.created_at || "").slice(0, 10) === key).length;
      return {
        count,
        label: d.toLocaleDateString("es-ES", { weekday: "narrow" }),
        isToday: i === 6,
      };
    });
    return { total, emitidas, aceptadas, rechazadas, bucketsWeek };
  }, [officeFiltered]);

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="prescriptions-page">
      {/* ── KPI ROW ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RxDonut total={stats.total} aceptadas={stats.aceptadas} />
        <EmitidasSpectrum emitidas={stats.emitidas} total={stats.total} />
        <RechazadasCard rechazadas={stats.rechazadas} total={stats.total} />
        <SemanaBarsRx buckets={stats.bucketsWeek} total={stats.total} />
      </div>

      {/* ── LIST CARD ───────────────────────────────────────────────── */}
      <div
        className="bg-white rounded-xl border border-black/[0.05] p-4 flex-1 flex flex-col min-h-0"
        data-testid="rx-list-card"
      >
        <div className="flex flex-col mb-3 gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-semibold text-black flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                Lista de recetas
              </div>
              <div className="text-[11px] text-black/45 mt-0.5">
                {visibleList.length} {visibleList.length === 1 ? "receta" : "recetas"}
                {isToday
                  ? " · hoy"
                  : isWeek
                  ? " · últimos 7 días"
                  : isMonth
                  ? " · últimos 30 días"
                  : isAll
                  ? " · todas"
                  : dateFilter
                  ? ` · ${new Date(dateFilter).toLocaleDateString("es-ES")}`
                  : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <label
                className="h-9 px-3 rounded-lg bg-white border border-black/[0.08] hover:bg-black/[0.03] text-[13px] font-semibold text-black inline-flex items-center gap-1.5 cursor-pointer transition"
                title="Filtrar por fecha específica"
              >
                <Calendar className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                <input
                  type="date"
                  value={rangeFilter ? "" : dateFilter}
                  onChange={(e) => { setRangeFilter(null); setDateFilter(e.target.value); }}
                  className="bg-transparent border-0 outline-none text-[13px] font-semibold text-black w-[105px] cursor-pointer focus:ring-0 p-0"
                  data-testid="rx-date-filter"
                  max={todayStr}
                />
              </label>
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="new-rx-button"
                className="h-9 pl-2.5 pr-3 rounded-lg bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition"
              >
                <Plus className="w-4 h-4" strokeWidth={2} weight="regular" /> Nueva receta
              </button>
            </div>
          </div>

          {/* Chips de filtro rápido */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { k: "today", label: "Hoy" },
              { k: "week", label: "Semana" },
              { k: "month", label: "Mes" },
              { k: "all", label: "Todas" },
            ].map((chip) => {
              const active =
                (chip.k === "today" && isToday) ||
                (chip.k === "week" && isWeek) ||
                (chip.k === "month" && isMonth) ||
                (chip.k === "all" && isAll);
              return (
                <button
                  key={chip.k}
                  type="button"
                  data-testid={`rx-chip-${chip.k}`}
                  onClick={() => {
                    if (chip.k === "today") { setRangeFilter(null); setDateFilter(todayStr); }
                    else if (chip.k === "week") { setDateFilter(""); setRangeFilter("week"); }
                    else if (chip.k === "month") { setDateFilter(""); setRangeFilter("month"); }
                    else if (chip.k === "all") { setDateFilter(""); setRangeFilter(null); }
                  }}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold transition ${
                    active
                      ? "bg-[#0071E3] text-white"
                      : "bg-white border border-black/[0.08] text-black hover:bg-black/[0.03]"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {firstLoad ? (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar" data-testid="rx-loading">
            <TableSkeleton rows={8} />
          </div>
        ) : visibleList.length === 0 ? (
          <div
            className="flex-1 flex flex-col items-center justify-center text-center py-8"
            data-testid="rx-empty"
          >
            <div className="w-10 h-10 rounded-md bg-[#F2F3F5] flex items-center justify-center mb-2">
              <FileText className="w-5 h-5 text-black/60" strokeWidth={1.75} weight="regular" />
            </div>
            <div className="text-[13px] text-black/60">
              {list.length === 0
                ? "Aún no hay recetas. Crea la primera."
                : isToday
                ? "No hay recetas para hoy."
                : dateFilter
                ? "No hay recetas para esta fecha."
                : "No hay recetas para esta oficina."}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            <table className="w-full text-sm" data-testid="rx-table">
              <thead className="sticky top-0 bg-[#F5F5F7] z-[1]">
                <tr className="text-left">
                  {[
                    "Fecha",
                    "Paciente",
                    "Diagnóstico",
                    "Medicamentos",
                    ...(pharmacyEnabled ? ["Farmacias", "Estado"] : []),
                    "",
                  ].map((h) => (
                    <th
                      key={h || "actions"}
                      className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleList.map((rx) => {
                  const p = patientById[rx.patient_id];
                  const initials = (p?.full_name || "?")
                    .split(" ")
                    .map((s) => s[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  const rxPharmacyNames = (rx.pharmacy_ids || [])
                    .map((pid) => {
                      const ph = pharmacies.find((x) => x.id === pid);
                      return ph?.name || ph?.email || "Farmacia";
                    })
                    .filter(Boolean);
                  return (
                    <tr
                      key={rx.id}
                      data-testid={`rx-row-${rx.id}`}
                      className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                    >
                      <td className="py-3 pr-3 whitespace-nowrap">
                        <div className="text-[13px] font-semibold text-black">
                          {new Date(rx.created_at).toLocaleDateString("es-ES", {
                            day: "numeric",
                            month: "short",
                          })}
                        </div>
                        <div className="text-[11px] text-black/45">
                          {new Date(rx.created_at).toLocaleTimeString("es-ES", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          {p?.photo_url ? (
                            <img
                              src={p.photo_url}
                              alt=""
                              className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-[#F2F3F5] text-black text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                              {initials}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold truncate max-w-[220px] text-[14px] text-black">
                              {p?.full_name || "Paciente"}
                            </div>
                            {p?.phone && (
                              <div className="text-[11px] text-black/50 truncate max-w-[220px]">
                                {p.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-[13px] text-black/75 max-w-[180px] truncate">
                        {rx.diagnosis || "—"}
                      </td>
                      <td className="py-3 px-3 max-w-[340px]">
                        <div className="text-[13px] text-black/85 truncate">
                          {(rx.items || [])
                            .map((i) => `${i.medication} · ${i.dosage}`)
                            .join(" · ")}
                        </div>
                        <div className="text-[11px] text-black/45 mt-0.5">
                          {(rx.items || []).length} medicamento(s)
                        </div>
                      </td>
                      {pharmacyEnabled && (
                        <>
                          <td className="py-3 px-3 text-[13px] text-black/75 max-w-[200px]">
                            {rxPharmacyNames.length === 0 ? (
                              <span className="text-black/40">—</span>
                            ) : (
                              <div
                                className="flex flex-col gap-0"
                                title={rxPharmacyNames.join(", ")}
                                data-testid={`rx-pharmacies-${rx.id}`}
                              >
                                {rxPharmacyNames.slice(0, 2).map((n, i) => (
                                  <span key={i} className="truncate">{n}</span>
                                ))}
                                {rxPharmacyNames.length > 2 && (
                                  <span className="text-[11px] text-black/45">
                                    +{rxPharmacyNames.length - 2} más
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <RxStatusBadge status={rx.status} />
                            {rx.status === "rejected" &&
                              rx.rejection_reason && (
                                <div
                                  className="text-[10px] text-red-600 mt-1 max-w-[180px] truncate"
                                  title={rx.rejection_reason}
                                >
                                  {rx.rejection_reason}
                                </div>
                              )}
                          </td>
                        </>
                      )}
                      <td className="py-3 pl-3 text-right whitespace-nowrap">
                        <Link
                          to={`/recetas/${rx.id}/imprimir`}
                          target="_blank"
                          data-testid={`print-rx-${rx.id}`}
                          title="Ver / Imprimir PDF"
                          className="w-8 h-8 rounded-md hover:bg-black/5 text-black/70 inline-flex items-center justify-center transition"
                        >
                          <Printer className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <AppleModal
          open
          onClose={() => setOpen(false)}
          icon={<FileText weight="regular" className="w-11 h-11" strokeWidth={1.5} />}
          title="Nueva receta"
          subtitle="Emite y envía la receta al paciente"
          size="xl"
          testid="rx-modal"
        >
          <form onSubmit={submit} className="flex flex-col">
            <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 max-h-[70vh]">
              {/* LEFT: form */}
              <div className="space-y-4 overflow-y-auto no-scrollbar pr-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                    Paciente
                  </label>
                  <PatientAutocomplete
                    patients={patients}
                    value={form.patient_id}
                    onChange={(id) => setForm({ ...form, patient_id: id })}
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                    Diagnóstico
                  </label>
                  <input
                    placeholder="ej: Bronquitis aguda"
                    value={form.diagnosis}
                    onChange={(e) =>
                      setForm({ ...form, diagnosis: e.target.value })
                    }
                    className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-diagnosis-input"
                  />
                </div>

                {/* Add medication */}
                <div className="rounded-lg bg-[#F2F3F5] p-3.5 space-y-2.5">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                    Añadir medicamento
                  </div>
                  <input
                    placeholder="Nombre del medicamento"
                    value={currentItem.medication}
                    onChange={(e) =>
                      setCurrentItem({ ...currentItem, medication: e.target.value })
                    }
                    className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] font-semibold text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-med-input"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      placeholder="Dosis (ej: 500 mg)"
                      value={currentItem.dosage}
                      onChange={(e) =>
                        setCurrentItem({ ...currentItem, dosage: e.target.value })
                      }
                      className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                      data-testid="rx-dosage-input"
                    />
                    <input
                      placeholder="Cantidad (ej: 30 tabletas)"
                      value={currentItem.quantity}
                      onChange={(e) =>
                        setCurrentItem({ ...currentItem, quantity: e.target.value })
                      }
                      className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                      data-testid="rx-qty-input"
                    />
                    <input
                      placeholder="Frecuencia (ej: cada 8 hrs)"
                      value={currentItem.frequency}
                      onChange={(e) =>
                        setCurrentItem({ ...currentItem, frequency: e.target.value })
                      }
                      className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                      data-testid="rx-freq-input"
                    />
                    <input
                      placeholder="Duración (ej: 7 días)"
                      value={currentItem.duration}
                      onChange={(e) =>
                        setCurrentItem({ ...currentItem, duration: e.target.value })
                      }
                      className="h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                      data-testid="rx-dur-input"
                    />
                  </div>
                  <input
                    placeholder="Indicaciones (opcional)"
                    value={currentItem.instructions}
                    onChange={(e) =>
                      setCurrentItem({ ...currentItem, instructions: e.target.value })
                    }
                    className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    data-testid="rx-instr-input"
                  />
                  <button
                    type="button"
                    onClick={addCurrentItem}
                    className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-lg bg-black text-white text-[13px] font-semibold hover:bg-black/85 transition"
                    data-testid="rx-add-item"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.75} weight="regular" /> Añadir a la receta
                  </button>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                    Notas adicionales
                  </label>
                  <textarea
                    placeholder="ej: Reposo 3 días, volver si fiebre persiste"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full min-h-[68px] rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                  />
                </div>

                {pharmacyEnabled && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-2 inline-flex items-center gap-1.5">
                      <Send className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Enviar a farmacias
                    </label>
                    <div className="space-y-1.5">
                      {pharmacies.map((p) => (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                            form.pharmacy_ids.includes(p.id)
                              ? "bg-[#F2F3F5] border-black/30"
                              : "bg-white border-black/[0.08] hover:bg-black/[0.02]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.pharmacy_ids.includes(p.id)}
                            onChange={() => togglePharm(p.id)}
                            data-testid={`rx-pharm-${p.id}`}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-black text-[13px] truncate">
                              {p.name}
                            </div>
                            <div className="text-[11px] text-black/45 truncate">
                              {p.address}
                            </div>
                          </div>
                        </label>
                      ))}
                      {pharmacies.length === 0 && (
                        <div className="text-[12px] text-black/45">
                          No hay farmacias registradas.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT: live preview */}
              <div className="bg-[#F2F3F5] rounded-2xl p-5 overflow-y-auto no-scrollbar">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-3">
                  Vista previa de la receta
                </div>
                <RxPreview
                  form={form}
                  doctor={user}
                  patient={patientById[form.patient_id]}
                  onRemoveItem={removeItem}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-black/[0.05]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-11 px-5 rounded-full bg-white border border-black/15 hover:bg-black/[0.03] text-[14px] font-medium text-black transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-11 px-6 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[14px] font-semibold transition"
                data-testid="rx-submit-button"
              >
                Crear receta
              </button>
            </div>
          </form>
        </AppleModal>
      )}
    </div>
  );
}

function PatientAutocomplete({ patients, value, onChange }) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);
  const wrapRef = useRef(null);

  const selected = patients.find((p) => p.id === value);

  // Keep visible label synced when selection changes from outside (e.g. URL preselect)
  useEffect(() => {
    if (selected) setQuery(selected.full_name);
    else setQuery("");
  }, [selected]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenList(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? patients
        .filter(
          (p) =>
            (p.full_name || "").toLowerCase().includes(q) ||
            (p.document_id || "").toLowerCase().includes(q) ||
            (p.phone || "").toLowerCase().includes(q),
        )
        .slice(0, 8)
    : patients.slice(0, 8);

  const pick = (p) => {
    onChange(p.id);
    setQuery(p.full_name);
    setOpenList(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpenList(true);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none" />
        <input
          type="text"
          required={!value}
          placeholder="Buscar por nombre, documento o teléfono…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpenList(true);
            if (value) onChange("");
          }}
          onFocus={() => setOpenList(true)}
          className="w-full h-10 bg-white border border-black/[0.08] rounded-lg pl-9 pr-9 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
          data-testid="rx-patient-search"
          autoComplete="off"
        />
        {(query || value) && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-black/[0.06]"
            tabIndex={-1}
            data-testid="rx-patient-clear"
          >
            <X className="w-3.5 h-3.5 text-black/50" />
          </button>
        )}
      </div>
      {openList && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-black/[0.08] rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto">
          {matches.length === 0 ? (
            <div className="px-3 py-4 text-[12px] text-black/45 text-center">
              Sin resultados
            </div>
          ) : (
            matches.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pick(p)}
                data-testid={`rx-patient-opt-${p.id}`}
                className={`w-full text-left px-3 py-2 hover:bg-black/[0.04] border-b border-black/[0.05] last:border-0 transition ${
                  p.id === value ? "bg-black/[0.04]" : ""
                }`}
              >
                <div className="text-[13px] font-semibold text-black">
                  {p.full_name}
                </div>
                <div className="text-[11px] text-black/45">
                  {[p.document_id, p.phone].filter(Boolean).join(" · ") || "—"}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function RxPreview({ form, doctor, patient, onRemoveItem }) {
  return (
    <div
      className="bg-white rounded-xl border border-black/[0.05] p-6 text-black text-[12px]"
      data-testid="rx-preview"
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-black/10 pb-3">
        <div className="flex items-center gap-2.5">
          <img
            src={doctor?.clinic_logo_url || "/brand/consudoctor-icon.png"}
            alt="Logo"
            className="w-11 h-11 rounded-lg object-contain bg-white border border-black/[0.05]"
            data-testid="rx-preview-logo"
          />
          <div>
            <div className="text-[14px] font-semibold leading-tight text-black">
              {doctor?.name || "Dr."}
            </div>
            {doctor?.specialty && (
              <div className="text-[11px] font-semibold text-black/60">
                {doctor.specialty}
              </div>
            )}
            <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45 mt-0.5">
              Receta médica
            </div>
          </div>
        </div>
        <div className="text-right text-[10px] text-black/50">
          <div className="font-semibold text-black/70">Vista previa</div>
          <div>{new Date().toLocaleString("es-ES")}</div>
        </div>
      </div>

      {/* Médico + Paciente */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Médico
          </div>
          <div className="font-semibold text-black">{doctor?.name}</div>
          {doctor?.email && (
            <div className="text-[11px] text-black/60">{doctor.email}</div>
          )}
          {doctor?.phone && (
            <div className="text-[11px] text-black/60">Tel: {doctor.phone}</div>
          )}
          {doctor?.address && (
            <div className="text-[11px] text-black/60">{doctor.address}</div>
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Paciente
          </div>
          <div className="font-semibold text-black">
            {patient?.full_name || (
              <span className="text-black/40 font-normal">
                — Seleccionar paciente —
              </span>
            )}
          </div>
          {patient?.document_id && (
            <div className="text-[11px] text-black/60">
              {patient.document_id}
            </div>
          )}
          {patient?.phone && (
            <div className="text-[11px] text-black/60">{patient.phone}</div>
          )}
        </div>
      </div>

      {/* Diagnóstico */}
      <div className="mt-4">
        <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
          Diagnóstico
        </div>
        <div className="font-semibold text-black">
          {form.diagnosis || (
            <span className="text-black/40 font-normal">—</span>
          )}
        </div>
      </div>

      {/* ℞ items */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[26px] font-serif italic text-black/80 leading-none select-none">
            ℞
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
              Prescripción
            </div>
            <div className="text-[10px] text-black/55">
              {form.items.length} medicamento
              {form.items.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {form.items.length === 0 ? (
          <div className="rounded-lg bg-[#F2F3F5] py-6 text-center text-black/40 text-[11px]">
            Aún no hay medicamentos. Usa el panel izquierdo para añadir.
          </div>
        ) : (
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b border-black/15 text-left">
                <th className="py-1.5 pr-2 w-6 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  #
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Medicamento
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Dosis
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Cant.
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Frec.
                </th>
                <th className="py-1.5 px-2 text-[8px] uppercase tracking-widest font-semibold text-black/45">
                  Dur.
                </th>
                <th className="py-1.5 pl-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <RxRow
                  key={i}
                  index={i}
                  item={it}
                  onRemove={() => onRemoveItem(i)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Notas */}
      {form.notes && (
        <div className="mt-4">
          <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
            Indicaciones / notas
          </div>
          <div className="text-[11px] whitespace-pre-wrap text-black/80">
            {form.notes}
          </div>
        </div>
      )}

      {/* Firma */}
      <div className="mt-8 pt-4 border-t border-black/10 flex justify-between items-end text-[10px]">
        <div className="flex flex-col items-start gap-1">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-5 w-auto object-contain"
          />
          <div className="text-black/45 font-semibold">
            www.consudoctor.com
          </div>
        </div>
        <div className="text-right">
          {doctor?.signature_url ? (
            <img
              src={doctor.signature_url}
              alt="firma"
              className="h-12 w-auto max-w-[180px] object-contain ml-auto"
            />
          ) : (
            <div className="h-12 w-40 rounded-lg bg-[#F2F3F5] flex items-center justify-center text-[10px] text-black/40 ml-auto">
              Sin firma
            </div>
          )}
          <div className="border-t border-black w-40 mt-1 ml-auto" />
          <div className="font-semibold text-black">{doctor?.name}</div>
          <div className="text-black/50">Firma del médico</div>
        </div>
      </div>
    </div>
  );
}

function RxRow({ index, item, onRemove }) {
  return (
    <>
      <tr
        className="border-b border-black/10 align-top"
        data-testid={`rx-preview-row-${index}`}
      >
        <td className="py-2 pr-2 font-semibold text-black/60">{index + 1}.</td>
        <td className="py-2 px-2 font-semibold text-black">{item.medication}</td>
        <td className="py-2 px-2 text-black/70">{item.dosage || "—"}</td>
        <td className="py-2 px-2 font-semibold text-black/80">
          {item.quantity || "—"}
        </td>
        <td className="py-2 px-2 text-black/70">{item.frequency || "—"}</td>
        <td className="py-2 px-2 text-black/70">{item.duration || "—"}</td>
        <td className="py-2 pl-2 text-right">
          <button
            type="button"
            onClick={onRemove}
            data-testid={`rx-preview-remove-${index}`}
            className="w-6 h-6 rounded-md text-black/40 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition"
            title="Quitar"
          >
            <Trash2
              className="w-3.5 h-3.5"
              strokeWidth={1.75}
              weight="regular"
            />
          </button>
        </td>
      </tr>
      {item.instructions && (
        <tr className="border-b border-black/10">
          <td></td>
          <td colSpan={6} className="pb-2 px-2 text-[10px] italic text-black/60">
            Indicaciones: {item.instructions}
          </td>
        </tr>
      )}
    </>
  );
}


// -----------------------------------------------------------------------------
// Badge de estado con mini-timeline visual para las recetas del doctor.
// -----------------------------------------------------------------------------
const RX_LABEL = {
  issued: "Emitida",
  accepted: "Aceptada",
  ready: "Lista",
  fulfilled: "Despachada",
  rejected: "Rechazada",
};

const RX_CLASS = {
  issued: "bg-[#F2F3F5] text-black/70",
  accepted: "bg-amber-50 text-amber-700",
  ready: "bg-indigo-50 text-indigo-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

// Orden de los pasos del flujo lineal (rejected NO aparece en el timeline).
const RX_STEPS = ["issued", "accepted", "ready", "fulfilled"];

function RxStatusBadge({ status }) {
  const s = status || "issued";
  const isRejected = s === "rejected";
  const currentIdx = RX_STEPS.indexOf(s);
  return (
    <div className="inline-flex flex-col items-start gap-1.5">
      <span
        className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${
          RX_CLASS[s] || RX_CLASS.issued
        }`}
        data-testid={`rx-status-${s}`}
      >
        {RX_LABEL[s] || s}
      </span>
      {!isRejected && (
        <div className="flex items-center gap-1" title="Progreso de la receta">
          {RX_STEPS.map((step, i) => {
            const done = i <= currentIdx;
            return (
              <div key={step} className="flex items-center gap-1">
                <div
                  className={`w-1.5 h-1.5 rounded-full transition ${
                    done ? "bg-black" : "bg-black/10"
                  }`}
                />
                {i < RX_STEPS.length - 1 && (
                  <div
                    className={`w-3 h-[2px] transition ${
                      i < currentIdx ? "bg-black" : "bg-black/10"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── KPI Widgets ──────────────────────────────────────────────────────────

function RxDonut({ total, aceptadas }) {
  const pct = total ? Math.round((aceptadas / total) * 100) : 0;
  return (
    <AppleKpi
      testid="rx-kpi-total"
      icon={FileText}
      title="Total recetas"
      description="Historial completo de prescripciones digitales emitidas."
      label="Emitidas"
      value={total}
      footer={total === 0 ? "Sin recetas aún" : `${pct}% aceptadas por la farmacia`}
    />
  );
}

function EmitidasSpectrum({ emitidas, total }) {
  const pct = total ? Math.round((emitidas / total) * 100) : 0;
  return (
    <AppleKpi
      testid="rx-kpi-emitidas"
      icon={Clock}
      title="En espera"
      description="Recetas enviadas que aún no han sido aceptadas."
      label="Sin aceptar"
      value={emitidas}
      footer={total === 0 ? "Sin pendientes" : `${pct}% del total`}
    />
  );
}

function RechazadasCard({ rechazadas, total }) {
  const pct = total ? Math.round((rechazadas / total) * 100) : 0;
  return (
    <AppleKpi
      testid="rx-kpi-rechazadas"
      icon={XCircle}
      title="Rechazadas"
      description="Recetas devueltas por la farmacia para corrección."
      label="Rechazadas"
      value={rechazadas}
      footer={total === 0 ? "Sin rechazos" : `${pct}% del total`}
    />
  );
}

function SemanaBarsRx({ buckets, total }) {
  const week = buckets.reduce((s, b) => s + b.count, 0);
  const busy = buckets.filter((b) => b.count > 0).length;
  return (
    <AppleKpi
      testid="rx-kpi-week"
      icon={CalendarCheck}
      title="Últimos 7 días"
      description="Ritmo semanal de emisión de recetas."
      label="Emitidas"
      value={week}
      footer={`${busy} de 7 días con recetas`}
    />
  );
}
